import axios from 'axios';
import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {postTestFinalize} from '../../../api/test/TestFinalize';
import {postTestInitiate, TestInitiateRequest} from '../../../api/test/TestInitiate';
import URL from '../../../config/url';
import {projectFetch} from '../../../store/project';
import {addListener, removeListener} from '../../../store/statusChecker';
import Util from '../../../util';
import {
  getTestDeploymentId,
  resetAllTasks,
  setDeployConfig,
  setDeployTask,
  TASK_STATUS,
  useDeploy,
} from '../useDeploy';
import {Deployment} from '../../../types/deployment/DeploymentResponse';
import {TaskInfo} from '../../../types/metadata/TaskInfo';

export const useDeployTask = (projectId: string) => {
  const [{deployTask, deployConfig}, dispatch] = useDeploy();
  const reduxDispatch = useDispatch();

  const finalize = useCallback(
    async (deploymentId: string) => {
      const response = await postTestFinalize({projectId, deploymentId});
      if (response.errors?.length) {
        throw new Error('Error received during GSP test finalize');
      }
      reduxDispatch(projectFetch(projectId));
    },
    [projectId, reduxDispatch]
  );

  const monitorStatus = useCallback(
    id => {
      return new Promise<void>(resolve => {
        reduxDispatch(
          addListener('GSP_DEPLOY', (status: TaskInfo) => {
            if (status.deploymentId !== id) {
              return;
            }
            dispatch(setDeployTask({monitoringResponse: status, deploymentId: id}));
            if (Util.isRealNumberEqual(status.percentCompleted, 100)) {
              reduxDispatch(removeListener('GSP_DEPLOY'));
              resolve();
            }
          })
        );
      });
    },
    [dispatch, reduxDispatch]
  );

  const start = useCallback(
    async (
      params: TestInitiateRequest,
      onStartDeploy?: (deployment: Deployment) => void
    ) => {
      const response = await postTestInitiate(params);
      if (response.errors?.length) {
        let error = response.errors.find(error => error.key);
        if (error) {
          dispatch(setDeployTask({deploymentId: error.key}));
        }
        throw new Error('Error received during GSP test');
      }
      const id = response.body?.id;
      dispatch(setDeployTask({status: TASK_STATUS.IN_PROGRESS, deploymentId: id}));
      if (onStartDeploy && response.body) {
        onStartDeploy(response.body);
      }

      return id;
    },
    [dispatch]
  );

  const run = useCallback(
    async (params: TestInitiateRequest) => {
      dispatch(setDeployTask({status: TASK_STATUS.IN_PROGRESS}));
      const deploymentId = await start(params);
      dispatch(
        setDeployConfig({testHistoryIds: [deploymentId, ...deployConfig.testHistoryIds]})
      );
      await monitorStatus(deploymentId);
      await finalize(deploymentId);
      dispatch(setDeployTask({status: TASK_STATUS.COMPLETE, deploymentId}));
    },
    [dispatch, start, monitorStatus, finalize, deployConfig]
  );

  const terminate = useCallback(
    async (optId?: string) => {
      const id = optId ?? getTestDeploymentId(deployTask);
      if (!id) {
        return;
      }
      try {
        await axios.get(URL.OPERATION_CANCEL('GSP_DEPLOY'), {
          params: {projectId, resourceId: id},
        });
        reduxDispatch(removeListener('GSP_DEPLOY'));
        dispatch(resetAllTasks());
      } catch (e) {}
    },
    [dispatch, projectId, reduxDispatch, deployTask]
  );

  const resumeIfExists = useCallback(async () => {
    try {
      const response = await axios.get(URL.PROJECT_OP_STATUS(projectId));
      const {tasks} = response.data.body;
      const existingTask = tasks.find(
        (task: any) => task.type === 'GSP_DEPLOY' && !task.spaId
      );
      if (!existingTask) {
        return;
      }
      const {deploymentId, applicationId, gspBoxId} = existingTask;
      dispatch(setDeployConfig({deploymentId: applicationId, nodeId: gspBoxId}));
      dispatch(setDeployTask({status: TASK_STATUS.IN_PROGRESS, deploymentId}));
      await monitorStatus(deploymentId);
      await finalize(deploymentId);
      dispatch(setDeployTask({status: TASK_STATUS.COMPLETE, deploymentId}));
    } catch (e) {
      dispatch(resetAllTasks());
    }
  }, [dispatch, projectId, monitorStatus, finalize]);

  return {
    runDeploy: run,
    startDeploy: start,
    terminateDeploy: terminate,
    resumeDeploy: resumeIfExists,
  };
};
